/* eslint-disable @typescript-eslint/no-explicit-any */
import getAppVersion from './helpers/getAppVersion';
import DataStore from '../support/DataStore';

type FetchError = Error & { status?: number };

type FetchCallback = (
  error: FetchError | null | undefined,
  data: ObjectOf<any>,
) => void;

let dataStore = DataStore.getInstance('app');

export function fetchJSON(
  url: string,
  callback?: FetchCallback,
): Promise<{
  [key: string]: any;
}> {
  let opts = {
    headers: getHeaders(),
  };
  return fetchData(url, opts, callback);
}

export function postJSON(
  url: string,
  data: any,
  callback?: FetchCallback,
): Promise<{
  [key: string]: any;
}> {
  if (Object(data) === data) {
    data = JSON.stringify(data);
  } else {
    data = String(data);
  }
  let headers = Object.assign(getHeaders(), {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
  let opts = {
    method: 'post',
    headers: headers,
    body: data,
  };
  return fetchData(url, opts, callback);
}

export function fetchData(
  url: string,
  opts?: RequestInit,
  callback?: FetchCallback | null | undefined,
): Promise<ObjectOf<any>> {
  let response: Response | undefined;
  let data: ObjectOf<any> | undefined;
  return fetch(url, opts)
    .then((_response) => {
      response = _response;
      let serverVersion = response.headers.get('X-App-Version');
      let appVersion = getAppVersion();
      if (serverVersion != null && serverVersion !== appVersion) {
        dataStore.set('isUpdateAvailable', true);
      }
      let contentType = response.headers.get('Content-Type') || '';
      let isJSON = contentType.split(';')[0] === 'application/json';
      if (isJSON) {
        return response.json();
      } else {
        return response.text();
      }
    })
    .then((_data: string | ObjectOf<any>) => {
      data = typeof _data === 'string' ? { __BODY__: _data } : _data;
      if (response) {
        if (response.status < 200 || response.status >= 400) {
          let message = `Fetch failed: Unexpected status: ${response.status} ${response.statusText}`;
          throw Object.assign(new Error(message), { status: response.status });
        }
      }
      if (callback) {
        // So that if `callback` throws it is not caught by the Promise machinery.
        setTimeout(() => callback?.(null, data as any), 1);
      }
      return data as any;
    })
    .catch((ex) => {
      let error: FetchError = ex instanceof Error ? ex : new Error(String(ex));
      // Special handling for an error indicating unexpected status (thrown above)
      if (error.status) {
        const entryPoint = dataStore.get('entryPoint');
        // TODO: We should not reload if fetching from another origin
        if (error.status === 403 && entryPoint === 'app') {
          window.location.assign(window.location.pathname);
          return;
        }
        // eslint-disable-next-line no-console
        console.error(error, { url });
      } else {
        // eslint-disable-next-line no-console
        console.error(error, { url });
        let message = `Fetch failed: ${error.message}`;
        // TODO: Why are we wrapping the error here?
        // TODO: If we must wrap it, use the new "cause" parameter of Error constructor (needs TS update).
        error = Object.assign(new Error(message), { cause: error });
      }
      // TODO: If there's no callback we should re-throw the error. Otherwise
      // `await` calls to this function will see errors getting swallowed.
      if (callback) {
        setTimeout(() => callback?.(error, data || {}), 1);
      }
    });
}

// Enforce that getHeaders is only used for same-origin requests, or more
// broadly ensure that no functions in this module are used for fetching from
// another origin. Otherwise we'll leak our session tokens.
export function getHeaders() {
  let headers: ObjectOf<string> = {};
  // Main Account
  let account = dataStore.get('account');
  if (account && account.token) {
    headers['X-Auth-Token'] = account.token;
  }
  let accountID = dataStore.get('accountID');
  if (accountID) {
    headers['X-Account-ID'] = accountID;
  }
  // Vendor Account
  let vendorAccount = dataStore.get('vendorAccount');
  if (vendorAccount && vendorAccount.token) {
    headers['X-Vendor-Auth-Token'] = vendorAccount.token;
  }
  let season = dataStore.get('season');
  if (season) {
    headers['X-Season-ID'] = season.id;
  }
  return headers;
}
